'use client';

import { useCallback, useContext, useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { Error, Check, Star } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Skeleton } from '@mui/material';
import {
  AmplifyServiceContext,
  Types,
  useEventEditComment,
} from '@scorenco/core';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

import {
  Item,
  ItemImage,
  ItemContent,
  ItemProps,
  ImageLoader,
  RichText,
  Image,
  RichTextInput,
} from '../../../primitives';
import { vars } from '../../../style';
import { EventPostItemFragment } from '../EventPostItem.fragment.generated';

import { EventPostCommentItemMenu } from './EventPostCommentItemMenu';

export const EventPostCommentItem = ({
  eventPost,
  event,
}: EventPostCommentItemProps) => {
  // Pas de menu sans authentification (ex. widgets)
  const hasAmplify = !!useContext(AmplifyServiceContext);
  const messages = (eventPost?.data as { message?: string } | null | undefined)
    ?.message;
  const picture = eventPost?.picture;
  const [isEditing, setEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(messages);
  const useEditComment = useEventEditComment();

  // TODO: Remplacer par la vraie logique premium
  const isPremium = false;

  useEffect(() => {
    void Promise.resolve().then(() => setEditedMessage(messages));
  }, [messages]);

  const temp = eventPost?.temp;
  const [error, setError] = useState(eventPost?.temp && eventPost?.error);

  useEffect(() => {
    void Promise.resolve().then(() =>
      setError(eventPost?.temp && eventPost?.error)
    );
  }, [eventPost?.temp, eventPost?.error]);

  const onSubmit = useCallback(
    async (value: string) => {
      if (value && eventPost?.id) {
        await useEditComment.execute({
          postId: eventPost.id,
          data: { message: value },
        });
      }
      setEditing(false);
    },
    [eventPost?.id, useEditComment]
  );

  // Tempory fix to manage slate comments already sended
  if (Array.isArray(messages as unknown)) {
    return null;
  }

  return (
    <Item
      css={css`
        opacity: ${temp && !error ? 0.5 : 1};
        align-items: flex-start;
      `}
    >
      <ItemImage
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          marginTop: '1rem',
          height: '100%',
        }}
      >
        <Image
          css={css`
            border-radius: 100%;
            width: 3rem;
            height: 3rem;
            object-fit: cover;
            aspect-ratio: 1;
            ${isPremium
              ? `
              border: 2px solid var(${vars.color.gold});
              box-shadow:
                0 0 2.5px var(${vars.color.gold}),
                0 0 5px var(${vars.color.gold});
            `
              : ''}
          `}
          width={48}
          height={48}
          src={
            eventPost?.spectator?.picture_url || '/images/user-placeholder.svg'
          }
        />
      </ItemImage>

      <ItemContent
        css={css`
          overflow: hidden;
        `}
      >
        <Box
          css={css`
            display: flex;
            align-items: center;
            margin-top: 1rem;
          `}
        >
          <Box
            css={css`
              flex: 1;
              color: var(${vars.color.secondary300});
              align-items: center;
              display: flex;
              gap: 0.25rem;
            `}
          >
            {eventPost?.spectator?.name || <Skeleton />}
            {isPremium && (
              <Star
                css={css`
                  color: var(${vars.color.gold});
                  font-size: 1.2rem;
                `}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {temp &&
              (error ? (
                <Error color="error" />
              ) : (
                <CircularProgress size="1rem" />
              ))}
            <Box>
              {!!eventPost?._created && (
                <Box
                  css={css`
                    padding: 0;
                    color: #888;
                    font-size: 0.8rem;
                  `}
                >
                  {formatDistanceToNow(
                    new Date(
                      typeof eventPost?._created === 'string'
                        ? eventPost._created
                        : ''
                    ),
                    {
                      locale: fr,
                    }
                  )}
                </Box>
              )}
            </Box>

            {hasAmplify &&
              eventPost &&
              (!temp || !!error) &&
              eventPost?.id !== -1 && (
                <EventPostCommentItemMenu
                  eventId={event?.id}
                  post={eventPost}
                  error={!!error}
                  setError={setError}
                  setEditing={setEditing}
                  css={css`
                    width: 2rem !important;
                    height: 2rem !important;
                  `}
                />
              )}
          </Box>
        </Box>
        {picture && (
          <ImageLoader
            src={picture}
            css={css`
              width: 100%;
              height: auto;
              max-height: 13rem;
              object-fit: contain;
              margin-top: 1rem;
              margin-bottom: 1rem;
            `}
            alt=""
            width={500}
            height={500}
          />
        )}
        {messages ? (
          isEditing ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                pb: 3,
              }}
            >
              <RichTextInput
                css={(theme) => css`
                  margin-left: ${theme.spacing(1)};
                  margin-right: ${theme.spacing(1)};
                  flex: 1;
                `}
                onChange={(value) =>
                  setEditedMessage(
                    typeof value === 'string' ? value : undefined
                  )
                }
                onReturn={(value) => {
                  if (value) onSubmit(value as any);
                }}
                value={editedMessage}
              />
              <IconButton
                onClick={() => editedMessage && onSubmit(editedMessage)}
              >
                <Check />
              </IconButton>
            </Box>
          ) : (
            <RichText
              css={css`
                padding: 0;
                margin-top: 0.5rem;
                margin-bottom: 1rem;
              `}
              content={messages}
            />
          )
        ) : (
          !picture && (
            <Box
              css={css`
                padding: 0;
                margin-top: 0.5rem;
                margin-bottom: 1rem;
              `}
            >
              <Skeleton />
            </Box>
          )
        )}
      </ItemContent>
    </Item>
  );
};

export type EventPostCommentItemProps = ItemProps & {
  eventPost?: EventPostItemFragment & { temp?: boolean; error?: boolean };
  event?: Types.Competitions_Event_Detail;
};
